import React, { useState, useMemo } from "react";
import { useNotifications } from "../contexts/NotificationContext";

/* ── Icon helpers ────────────────────────────────────────────────────────── */
function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7,10 12,15 17,10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1,4 1,10 7,10"/>
      <path d="M3.51 15a9 9 0 1 0 .49-3"/>
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

/* ── Confidence colour ───────────────────────────────────────────────────── */
function confidenceColor(conf) {
  if (conf >= 0.85) return "var(--accent-red)";
  if (conf >= 0.65) return "var(--accent-orange)";
  return "var(--accent-green)";
}

function confidenceLabel(conf) {
  if (conf >= 0.85) return "HIGH";
  if (conf >= 0.65) return "MODERATE";
  return "LOW";
}

/* ── Sort arrow indicator ─────────────────────────────────────────────────── */
function SortArrow({ active, dir }) {
  if (!active) return <span style={{ opacity: 0.3, marginLeft: 4, fontSize: "0.65rem" }}>⇅</span>;
  return <span style={{ marginLeft: 4, fontSize: "0.65rem" }}>{dir === "asc" ? "↑" : "↓"}</span>;
}

const inputStyle = {
  width: "100%",
  padding: "4px 8px",
  fontSize: "0.72rem",
  background: "var(--bg-input)",
  color: "var(--text-primary)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  outline: "none",
};

/* ── Nodule Details Table with filter + sort ─────────────────────────────── */
function NoduleDetailsTable({ nodule_details, onSliceClick, confidenceColor, confidenceLabel }) {
  const [filterSlice, setFilterSlice]   = useState("");
  const [filterConf,  setFilterConf]    = useState("");
  const [filterRisk,  setFilterRisk]    = useState("");
  const [sortKey,     setSortKey]       = useState(null);  // "slice" | "confidence"
  const [sortDir,     setSortDir]       = useState("asc"); // "asc" | "desc"

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    let list = nodule_details.map((n, i) => ({ ...n, _idx: i }));

    if (filterSlice.trim()) {
      const val = parseInt(filterSlice, 10);
      if (!isNaN(val)) list = list.filter((n) => (n.slice_idx ?? n.slice_peak ?? -1) + 1 === val);
    }
    if (filterConf.trim()) {
      const val = parseFloat(filterConf);
      if (!isNaN(val)) list = list.filter((n) => ((n.confidence || 0) * 100) >= val);
    }
    if (filterRisk.trim()) {
      const r = filterRisk.trim().toUpperCase();
      list = list.filter((n) => confidenceLabel(n.confidence || 0).startsWith(r));
    }

    if (sortKey === "slice") {
      list.sort((a, b) => {
        const sa = (a.slice_idx ?? a.slice_peak) ?? 0;
        const sb = (b.slice_idx ?? b.slice_peak) ?? 0;
        return sortDir === "asc" ? sa - sb : sb - sa;
      });
    } else if (sortKey === "confidence") {
      list.sort((a, b) => {
        const ca = a.confidence || 0;
        const cb = b.confidence || 0;
        return sortDir === "asc" ? ca - cb : cb - ca;
      });
    }
    return list;
  }, [nodule_details, filterSlice, filterConf, filterRisk, sortKey, sortDir]);

  const thStyle = {
    padding:       "6px 10px",
    textAlign:     "left",
    color:         "var(--text-muted)",
    fontSize:      "0.68rem",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight:    600,
    borderBottom:  "1px solid var(--border)",
  };
  const sortableThStyle = { ...thStyle, cursor: "pointer", userSelect: "none" };

  return (
    <div className="card">
      <p className="card-title">Nodule Details</p>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={sortableThStyle} onClick={() => toggleSort("slice")}>
                Slice <SortArrow active={sortKey === "slice"} dir={sortDir} />
              </th>
              <th style={thStyle}>Size (mm)</th>
              <th style={sortableThStyle} onClick={() => toggleSort("confidence")}>
                Confidence <SortArrow active={sortKey === "confidence"} dir={sortDir} />
              </th>
              <th style={thStyle}>Risk</th>
            </tr>
            {/* ── Filter row ──────────────────────────────────────────────── */}
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              <th />
              <th style={{ padding: "4px 6px" }}>
                <input
                  style={inputStyle}
                  placeholder="Slice #"
                  value={filterSlice}
                  onChange={(e) => setFilterSlice(e.target.value)}
                />
              </th>
              <th />
              <th style={{ padding: "4px 6px" }}>
                <input
                  style={inputStyle}
                  placeholder="Min %"
                  value={filterConf}
                  onChange={(e) => setFilterConf(e.target.value)}
                />
              </th>
              <th style={{ padding: "4px 6px" }}>
                <input
                  style={inputStyle}
                  placeholder="HIGH / MOD / LOW"
                  value={filterRisk}
                  onChange={(e) => setFilterRisk(e.target.value)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: "12px 10px", color: "var(--text-muted)", textAlign: "center" }}>
                  No nodules match filters
                </td>
              </tr>
            )}
            {filtered.map((n, i) => {
              const sliceNum = n.slice_idx ?? n.slice_peak;
              return (
                <tr key={n._idx} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "9px 10px", color: "var(--text-muted)" }}>{i + 1}</td>
                  <td
                    style={{
                      padding: "9px 10px",
                      fontFamily: "monospace",
                      color: onSliceClick ? "var(--accent-blue)" : undefined,
                      cursor: onSliceClick ? "pointer" : undefined,
                      textDecoration: onSliceClick ? "underline" : undefined,
                    }}
                    onClick={() => onSliceClick && sliceNum != null && onSliceClick(sliceNum)}
                    title={sliceNum != null ? `Go to slice ${sliceNum + 1}` : undefined}
                  >
                    {sliceNum != null ? sliceNum + 1 : "—"}
                  </td>
                  <td style={{ padding: "9px 10px", fontWeight: 600 }}>
                    {typeof n.diameter_mm === "number" ? n.diameter_mm.toFixed(1) : "—"}
                  </td>
                  <td style={{ padding: "9px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{
                        width: 60, height: 4,
                        background: "var(--bg-input)",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}>
                        <div style={{
                          height: "100%",
                          width: `${(n.confidence || 0) * 100}%`,
                          background: confidenceColor(n.confidence || 0),
                          borderRadius: 2,
                        }} />
                      </div>
                      <span style={{ color: "var(--text-secondary)" }}>
                        {((n.confidence || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: "9px 10px" }}>
                    <span
                      className="tag"
                      style={{
                        background: `${confidenceColor(n.confidence || 0)}20`,
                        color: confidenceColor(n.confidence || 0),
                      }}
                    >
                      {confidenceLabel(n.confidence || 0)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function ResultsPanel({ jobData, onNewScan, onSliceClick }) {
  const { addNotification } = useNotifications();
  const {
    nodule_count          = 0,
    total_slices          = 0,
    nodule_details        = [],
    download_png_zip_url  = null,
    download_dicom_url    = null,
    patient_name          = "Anonymous",
    completed_at          = null,
    scan_filename         = null,
  } = jobData || {};

  const handleDownload = (url, label) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href   = url;
    a.target = "_blank";
    a.rel    = "noopener noreferrer";
    a.click();
    addNotification({
      type: "download",
      title: "Download Started",
      message: `${label} file download initiated.`,
    });
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

      {/* ── Summary card ──────────────────────────────────────────────────── */}
      <div className="card">
        <p className="card-title">Analysis Summary</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.25rem" }}>
          {[
            { label: "Nodules detected", value: nodule_count, accent: nodule_count > 0 ? "var(--accent-tumor)" : "var(--accent-green)" },
            { label: "CT slices scanned", value: total_slices, accent: "var(--text-primary)" },
            { label: "Patient",           value: patient_name,   accent: "var(--text-primary)" },
            { label: "Analysed at",       value: completed_at ? new Date(completed_at).toLocaleString() : "—", accent: "var(--text-secondary)" },
          ].map(({ label, value, accent }) => (
            <div key={label} style={{
              padding:      "12px",
              background:   "var(--bg-input)",
              borderRadius: "var(--radius-sm)",
              border:       "1px solid var(--border)",
            }}>
              <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                {label}
              </div>
              <div style={{ fontSize: "1rem", fontWeight: 700, color: accent }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Overall risk indicator */}
        <div style={{
          padding:      "12px 16px",
          background:   nodule_count > 0
            ? "rgba(239, 68, 68, 0.08)"
            : "rgba(16, 185, 129, 0.08)",
          border:       `1px solid ${nodule_count > 0 ? "rgba(239,68,68,0.25)" : "rgba(16,185,129,0.25)"}`,
          borderRadius: "var(--radius-sm)",
          display:      "flex",
          alignItems:   "center",
          gap:          10,
        }}>
          <div style={{
            width: 10, height: 10,
            borderRadius: "50%",
            background: nodule_count > 0 ? "var(--accent-tumor)" : "var(--accent-green)",
            flexShrink: 0,
          }} />
          <span style={{ fontSize: "0.84rem", fontWeight: 600 }}>
            {nodule_count > 0
              ? `${nodule_count} pulmonary nodule${nodule_count > 1 ? "s" : ""} detected — radiologist review recommended`
              : "No significant nodules detected in this scan"}
          </span>
        </div>
      </div>

      {/* ── Nodule details table ───────────────────────────────────────────── */}
      {nodule_details.length > 0 && (
        <NoduleDetailsTable
          nodule_details={nodule_details}
          onSliceClick={onSliceClick}
          confidenceColor={confidenceColor}
          confidenceLabel={confidenceLabel}
        />
      )}

      {/* ── Downloads ─────────────────────────────────────────────────────── */}
      <div className="card">
        <p className="card-title">Export Results</p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <button
            className="btn btn-download"
            onClick={() => handleDownload(download_png_zip_url, "annotated_slices")}
            disabled={!download_png_zip_url}
          >
            <DownloadIcon />
            Download Annotated PNGs (ZIP)
          </button>

          <button
            className="btn btn-download"
            style={{
              background:  "rgba(59,130,246,0.1)",
              color:       "var(--accent-blue)",
              borderColor: "rgba(59,130,246,0.3)",
            }}
            onClick={() => handleDownload(download_dicom_url, "dicom_with_overlay")}
            disabled={!download_dicom_url}
          >
            <DownloadIcon />
            Download DICOM with Overlay
          </button>
        </div>

        <p style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: "var(--text-muted)" }}>
          Links are valid for 60 minutes. Re-open this report to refresh.
        </p>
      </div>

      {/* ── AI disclaimer ─────────────────────────────────────────────────── */}
      <div style={{
        padding:      "12px 16px",
        background:   "rgba(245,158,11,0.07)",
        border:       "1px solid rgba(245,158,11,0.25)",
        borderRadius: "var(--radius-sm)",
        display:      "flex",
        gap:          10,
        alignItems:   "flex-start",
      }}>
        <span style={{ color: "var(--accent-orange)", flexShrink: 0, paddingTop: 1 }}>
          <WarningIcon />
        </span>
        <p style={{ fontSize: "0.78rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
          <strong style={{ color: "var(--accent-orange)" }}>Radiologist review required.</strong>{" "}
          AI-generated results are for assistive purposes only and must be confirmed by a
          qualified radiologist before any clinical decision.
        </p>
      </div>

      {/* ── New scan ──────────────────────────────────────────────────────── */}
      <button
        className="btn btn-secondary"
        style={{ width: "100%" }}
        onClick={onNewScan}
      >
        <RefreshIcon />
        Analyse New Scan
      </button>
    </div>
  );
}
